<template>
  <TriadMetronomeBar
    :bpm="bpm"
    :is-running="isRunning"
    :current-beat="currentBeat"
    :total-beats="totalBeats"
    @toggle-play="togglePlay"
    @expand="metronomeStore.openFullscreen()"
    @close="close"
  />
  <Transition name="fade">
    <div
      v-if="metronomeStore.isOpen && metronomeStore.isFullscreen"
      class="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      @click.self="metronomeStore.minimize()"
    >
    <div
      class="w-full max-w-md bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl p-5 md:p-6 max-h-[92vh] overflow-y-auto text-gray-800 dark:text-gray-100 select-none"
    >
      <!-- Header (mesma identidade do afinador) -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <MetronomeIcon class="w-6 h-6 text-gray-800 dark:text-white" />
          <h2 class="text-xl font-bold tracking-tight">{{ $t('metronome.title') }}</h2>
        </div>

        <div class="flex items-center gap-1">
          <button
            type="button"
            @click="metronomeStore.minimize()"
            class="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            :title="$t('metronome.minimizeTitle')"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            type="button"
            @click="close"
            class="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            :title="$t('metronome.closeTitle')"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <p class="text-xs text-gray-500 dark:text-gray-400 mt-4 mb-1">{{ $t('metronome.subtitle') }}</p>

      <!-- Sound selector -->
      <TriadSelect
        :modelValue="metronomeStore.sound"
        :options="soundOptions"
        :label="$t('metronome.sound')"
        @change="selectSound"
        root-class="block w-full"
      />

      <!-- Time signature selector -->
      <TriadSelect
        :modelValue="metronomeStore.timeSignature"
        :options="timeSignatureOptions"
        :label="$t('metronome.timeSignature')"
        @change="selectTimeSignature"
        root-class="block w-full mt-3"
      />

      <!-- BPM Display & Tap Tempo -->
      <div class="flex flex-col items-center justify-center mt-5 mb-2">
        <div
          class="text-5xl font-black tracking-tight"
          :class="isRunning ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'"
        >
          {{ bpm }}
        </div>
        <span class="text-xs font-semibold uppercase tracking-wider text-gray-400 mt-0.5">{{ $t('metronome.bpm') }}</span>

        <!-- Beat Dots Visual Indicator -->
        <div class="flex items-center gap-2 mt-3">
          <div
            v-for="b in totalBeats"
            :key="b"
            class="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-700 transition-all duration-75"
            :class="[
              currentBeat === b - 1 && isRunning
                ? b === 1
                  ? 'bg-amber-400 border-amber-500 scale-125 shadow-lg shadow-amber-400/50'
                  : 'bg-emerald-500 border-emerald-600 scale-110 shadow-md shadow-emerald-500/40'
                : 'bg-transparent'
            ]"
          ></div>
        </div>
      </div>

      <!-- Tempo Slider and Quick Adjust Buttons -->
      <div class="my-3">
        <input
          type="range"
          min="40"
          max="240"
          v-model.number="bpm"
          @input="onBpmChange"
          class="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
        />

        <div class="flex items-center justify-between gap-1 mt-2 text-xs font-mono font-bold">
          <button
            type="button"
            @click="adjustBpm(-5)"
            class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md"
          >
            -5
          </button>
          <button
            type="button"
            @click="adjustBpm(-1)"
            class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md"
          >
            -1
          </button>
          <button
            type="button"
            @click="handleTapTempo"
            class="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900 rounded-md font-sans text-xs active:scale-95 transition-transform"
          >
            {{ $t('metronome.tapTempo') }}
          </button>
          <button
            type="button"
            @click="adjustBpm(1)"
            class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md"
          >
            +1
          </button>
          <button
            type="button"
            @click="adjustBpm(5)"
            class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md"
          >
            +5
          </button>
        </div>
      </div>

      <!-- Play / Stop & Sound Settings -->
      <div class="flex items-center gap-2 mt-5">
        <button
          type="button"
          @click="togglePlay"
          class="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2 active:scale-95"
          :style="{ backgroundColor: isRunning ? '#e5487a' : '#22c55e' }"
          :title="isRunning ? $t('metronome.stop') : $t('metronome.start')"
        >
          <svg v-if="isRunning" class="w-4 h-4 fill-current" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          <svg v-else class="w-4 h-4 fill-current" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
          {{ isRunning ? $t('metronome.stop') : $t('metronome.start') }}
        </button>

        <!-- Save BPM Button (if props.canSave) -->
        <button
          v-if="effectiveCanSave"
          type="button"
          @click="emitSaveBpm"
          class="px-3 py-2 border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors"
          :title="$t('metronome.saveTitle')"
        >
          {{ $t('metronome.save') }}
        </button>
      </div>
    </div>
    </div>
  </Transition>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { MetronomeEngine, METRONOME_SOUNDS } from '@/service/audio/metronomeEngine';
import MetronomeIcon from '@/components/icons/MetronomeIcon.vue';
import TriadMetronomeBar from '@/components/audio/TriadMetronomeBar.vue';
import TriadSelect from '@/components/common/TriadSelect.vue';
import metronomeStore, { TIME_SIGNATURES } from '@/store/metronome.store';
import { appEvents } from '@/store/app.events';

const { t } = useI18n();

const soundOptions = computed(() =>
  METRONOME_SOUNDS.map((s) => ({ value: s.value, label: t(s.labelKey) }))
);

const timeSignatureOptions = computed(() =>
  TIME_SIGNATURES.map((sig) => ({ value: sig, label: sig }))
);

const selectSound = (sound) => {
  metronomeStore.setSound(sound);
  // Troca ao vivo, mesmo rodando (o scheduler lê o config a cada nota)
  if (engine) engine.updateConfig({ sound });
};

const selectTimeSignature = (sig) => {
  metronomeStore.setTimeSignature(sig);
  if (engine) {
    engine.updateConfig({ timeSignature: sig });
    // Recomeça o compasso para o índice não estourar o novo total
    engine.currentBeatInBar = 0;
    currentBeat.value = 0;
  }
};

const props = defineProps({
  initialBpm: {
    type: Number,
    default: 120,
  },
  timeSignature: {
    type: String,
    default: '4/4',
  },
  canSave: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save-bpm', 'close']);

const bpm = ref(props.initialBpm || 120);
const isRunning = ref(false);
const currentBeat = ref(0);
// Contexto vindo da tela (ex. PlaylistSongView): bpm da música + permissão de salvar.
// Permite UMA instância global no App.vue servindo todas as rotas.
const contextCanSave = ref(false);
const effectiveCanSave = computed(() => props.canSave || contextCanSave.value);

const totalBeats = computed(() => {
  const [num] = (metronomeStore.timeSignature || '4/4').split('/').map(Number);
  return num || 4;
});

let engine = null;
let tapTimes = [];

onMounted(() => {
  engine = new MetronomeEngine(
    {
      bpm: bpm.value,
      timeSignature: metronomeStore.timeSignature,
      sound: metronomeStore.sound,
    },
    (beat) => {
      currentBeat.value = beat;
    }
  );
  appEvents.on('toggleMetronome', handleGlobalToggle);
  appEvents.on('metronome:context', handleContext);
});

watch(
  () => props.initialBpm,
  (newVal) => {
    if (newVal && newVal > 0) {
      bpm.value = newVal;
      if (engine) engine.updateConfig({ bpm: newVal });
    }
  }
);

const onBpmChange = () => {
  if (engine) engine.updateConfig({ bpm: bpm.value });
};

const adjustBpm = (delta) => {
  bpm.value = Math.max(30, Math.min(260, bpm.value + delta));
  onBpmChange();
};

const handleTapTempo = () => {
  const now = performance.now();
  // Reset if last tap was more than 2 seconds ago
  if (tapTimes.length > 0 && now - tapTimes[tapTimes.length - 1] > 2000) {
    tapTimes = [];
  }
  tapTimes.push(now);
  if (tapTimes.length > 4) {
    tapTimes.shift();
  }
  if (tapTimes.length >= 2) {
    let intervals = [];
    for (let i = 1; i < tapTimes.length; i++) {
      intervals.push(tapTimes[i] - tapTimes[i - 1]);
    }
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const calculatedBpm = Math.round(60000 / avgInterval);
    if (calculatedBpm >= 30 && calculatedBpm <= 260) {
      bpm.value = calculatedBpm;
      onBpmChange();
    }
  }
};

const togglePlay = () => {
  if (!engine) return;
  if (isRunning.value) {
    engine.stop();
    isRunning.value = false;
    currentBeat.value = 0;
  } else {
    engine.start();
    isRunning.value = true;
  }
};

const open = () => {
  metronomeStore.openFullscreen();
};

const close = () => {
  if (isRunning.value && engine) {
    engine.stop();
    isRunning.value = false;
  }
  metronomeStore.close();
  emit('close');
};

const toggle = () => {
  metronomeStore.toggle();
};

const emitSaveBpm = () => {
  emit('save-bpm', bpm.value);
  appEvents.emit('metronome:saveBpm', bpm.value);
};

const handleGlobalToggle = () => {
  metronomeStore.toggle();
};

const handleContext = ({ bpm: ctxBpm, canSave } = {}) => {
  if (ctxBpm && ctxBpm > 0) {
    bpm.value = ctxBpm;
    if (engine) engine.updateConfig({ bpm: ctxBpm });
  }
  contextCanSave.value = !!canSave;
};

onUnmounted(() => {
  if (engine) {
    engine.stop();
  }
  appEvents.off('toggleMetronome', handleGlobalToggle);
  appEvents.off('metronome:context', handleContext);
});

defineExpose({
  open,
  close,
  toggle,
  isOpen: computed(() => metronomeStore.isOpen),
  setBpm: (val) => {
    if (val > 0) {
      bpm.value = val;
      onBpmChange();
    }
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
